import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ScrollToTopButton from '../components/ScrollToTopButton';

// Styled Components
const HeroSection = styled.section`
  height: 50vh;
  background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('/images/background_jobs.jpg') no-repeat center center/cover;
  display: flex;
  align-items: center;
  text-align: center;
  color: white;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  h1 {
    font-size: 3rem;
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    margin-bottom: 30px;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

// Changed from Link to a for internal anchors
const Button = styled.a`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--dark-color);
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
`;

const JobIntro = styled.section`
  padding: 80px 0;
  background-color: white;
`;

const IntroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const IntroImage = styled.div`
  border-radius: 10px;
  overflow: hidden;
  box-shadow: var(--shadow);
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const IntroText = styled.div`
  h2 {
    color: var(--primary-color);
    margin-bottom: 20px;
    font-size: 2.5rem;
  }
`;

const JobCategories = styled.section`
  padding: 80px 0;
  background-color: var(--light-color);
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 50px;
  
  h2 {
    font-size: 2.5rem;
    color: var(--primary-color);
    position: relative;
    display: inline-block;
    padding-bottom: 15px;
    
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background-color: var(--accent-color);
    }
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
`;

const CategoryCard = styled.div`
  background-color: white;
  border-radius: 10px;
  box-shadow: var(--shadow);
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
`;

const CategoryContent = styled.div`
  padding: 25px;
  
  h3 {
    color: var(--primary-color);
    margin-bottom: 15px;
    font-size: 1.5rem;
  }
`;

const JobList = styled.ul`
  list-style: none;
  margin-top: 20px;
  
  li {
    margin-bottom: 12px;
    padding-left: 25px;
    position: relative;
    
    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: var(--accent-color);
      font-weight: bold;
    }
  }
`;

const Benefits = styled.section`
  padding: 80px 0;
  background-color: white;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
`;

const BenefitCard = styled.div`
  text-align: center;
  padding: 30px 20px;
  background-color: var(--light-color);
  border-radius: 10px;
  box-shadow: var(--shadow);
  transition: transform 0.3s;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const BenefitIcon = styled.div`
  width: 70px;
  height: 70px;
  background-color: var(--primary-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 30px;
`;

const ApplicationMethods = styled.section`
  padding: 80px 0;
  background-color: var(--light-color);
`;

const MethodsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
`;

const MethodCard = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: var(--shadow);
  text-align: center;
  transition: transform 0.3s;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const MethodIcon = styled.div`
  width: 80px;
  height: 80px;
  background-color: var(--primary-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 40px;
`;

const MethodButton = styled.a`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--dark-color);
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 20px;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
`;

const ApplicationForm = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px;
  background-color: white;
  border-radius: 10px;
  box-shadow: var(--shadow);
`;

const FormTitle = styled.div`
  text-align: center;
  margin-bottom: 30px;
  
  h3 {
    color: var(--primary-color);
    font-size: 1.8rem;
  }
`;

const FormInfo = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 30px;
  border-left: 4px solid var(--accent-color);
  
  p {
    margin: 0;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
  }
  
  textarea {
    height: 150px;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  
  input {
    width: auto;
    margin-right: 10px;
    margin-top: 5px;
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--dark-color);
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    transform: none;
  }
`;

// Neue Komponenten für Formular-Feedback
const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  text-align: center;
  
  h3 {
    margin-bottom: 15px;
    color: #155724;
  }
  
  button {
    margin-top: 15px;
    background-color: var(--accent-color);
    color: var(--dark-color);
    padding: 12px 30px;
    border: none;
    border-radius: 5px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      background-color: #e0a800;
      transform: translateY(-3px);
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-right: 10px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

function Jobs() {
  // State for "Other Position" field
  const [showOtherPosition, setShowOtherPosition] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    address: '',
    position: '',
    otherPosition: '',
    message: '',
    privacy: false
  });
  
  // E-Mail Adresse für das Formular
  const EMAIL_ADDRESS = "info@guardiansmoves.de";
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Überprüfen, ob die URL einen Erfolgsparameter enthält
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setSubmitSuccess(true);
    }
  }, []);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'position') {
      setShowOtherPosition(value === 'other');
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Funktion zur Bestimmung der Positionsbezeichnung
  const getPositionLabel = (value) => {
    const option = positionOptions.find(opt => opt.value === value);
    return option ? option.label : 'Unbekannte Position';
  };

  // Formular zurücksetzen
  const resetForm = () => {
    setSubmitSuccess(false);
    setFormData({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      address: '',
      position: '',
      otherPosition: '',
      message: '',
      privacy: false
    });
  };
  
  // Job categories data
  const jobCategories = [
    {
      title: 'Taxi & Mietwagenservice',
      description: 'Bringen Sie Menschen sicher an ihr Ziel und werden Sie Teil unseres Fahrer-Teams. Wir suchen zuverlässige und kundenorientierte Mitarbeiter.',
      positions: [
        'Taxi-Fahrer (m/w/d)',
        'Chauffeur für Mietwagen (m/w/d)',
        'Disponent (m/w/d)',
        'Fuhrparkmanager (m/w/d)'
      ]
    },
    {
      title: 'Sicherheitsdienste',
      description: 'Schützen Sie Personen und Objekte als Teil unseres Sicherheitsteams. Wir suchen aufmerksame und verantwortungsvolle Mitarbeiter.',
      positions: [
        'Sicherheitsmitarbeiter (m/w/d)',
        'Objektschützer (m/w/d)',
        'Veranstaltungsschutz (m/w/d)',
        'Sicherheitsberater (m/w/d)'
      ]
    },
    {
      title: 'Reinigungsdienste',
      description: 'Sorgen Sie für Sauberkeit und Wohlbefinden in verschiedenen Umgebungen. Wir suchen gewissenhafte und gründliche Mitarbeiter.',
      positions: [
        'Reinigungskraft (m/w/d)',
        'Objektleiter Reinigung (m/w/d)',
        'Hausmeister (m/w/d)',
        'Glasreiniger (m/w/d)'
      ]
    }
  ];
  
  // Benefits data with fixed SVG paths
  const benefits = [
    {
      // Split into separate path elements to avoid spaces between path commands
      icons: [
        'M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z',
        'M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z'
      ],
      title: 'Faire Vergütung',
      description: 'Wir bieten eine leistungsgerechte Bezahlung und zusätzliche Bonusprogramme für besonderes Engagement.'
    },
    {
      icon: 'M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z',
      title: 'Flexible Arbeitszeiten',
      description: 'Je nach Position bieten wir flexible Arbeitszeitmodelle an, die eine gute Work-Life-Balance ermöglichen.'
    },
    {
      // Split into separate path elements
      icons: [
        'M11 2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h6zM5 1a4 4 0 0 0-4 4v6a4 4 0 0 0 4 4h6a4 4 0 0 0 4-4V5a4 4 0 0 0-4-4H5z',
        'M10.5 8.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z'
      ],
      title: 'Weiterbildung',
      description: 'Wir fördern Ihre berufliche Entwicklung durch regelmäßige Schulungen und Weiterbildungsangebote.'
    },
    {
      // Split into separate path elements
      icons: [
        'M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
        'M5.216 14A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216z',
        'M4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z'
      ],
      title: 'Teamgeist',
      description: 'Bei uns arbeiten Sie in einem freundlichen, motivierten Team mit flachen Hierarchien und offener Kommunikation.'
    }
  ];
  
  // Application methods data with fixed SVG paths
  const applicationMethods = [
    {
      icon: 'M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z',
      title: 'Online-Formular',
      description: 'Füllen Sie unser Bewerbungsformular direkt auf dieser Seite aus. Wir werden Ihre Bewerbung prüfen und uns zeitnah bei Ihnen melden.',
      link: '#bewerbungsformular',
      text: 'Zum Formular'
    },
    {
      icon: 'M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z',
      title: 'WhatsApp',
      description: 'Sie können uns auch eine kurze Nachricht per WhatsApp senden. Teilen Sie uns mit, für welchen Bereich Sie sich interessieren.                                     ',
      link: 'https://api.whatsapp.com/send/?phone=4915563547378&text=Bewerbung+bei+GuardiansMoves&type=phone_number&app_absent=0',
      text: 'Per WhatsApp bewerben',
      external: true
    },
    {
      icons: [
        'M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z'
      ],
      title: 'E-Mail',
      description: 'Senden Sie Ihre vollständigen Bewerbungsunterlagen per E-Mail an uns. Bitte geben Sie den gewünschten Tätigkeitsbereich im Betreff an.',
      link: `mailto:${EMAIL_ADDRESS}?subject=Bewerbung`,
      text: 'Per E-Mail bewerben'
    }
  ];
  
  // Position options for select dropdown
  const positionOptions = [
    { value: '', label: 'Bitte auswählen' },
    { value: 'taxi_driver', label: 'Taxi-Fahrer (m/w/d)' },
    { value: 'chauffeur', label: 'Chauffeur für Mietwagen (m/w/d)' },
    { value: 'dispatcher', label: 'Disponent (m/w/d)' },
    { value: 'fleet_manager', label: 'Fuhrparkmanager (m/w/d)' },
    { value: 'security_staff', label: 'Sicherheitsmitarbeiter (m/w/d)' },
    { value: 'object_protector', label: 'Objektschützer (m/w/d)' },
    { value: 'event_security', label: 'Veranstaltungsschutz (m/w/d)' },
    { value: 'security_consultant', label: 'Sicherheitsberater (m/w/d)' },
    { value: 'cleaning_staff', label: 'Reinigungskraft (m/w/d)' },
    { value: 'cleaning_manager', label: 'Objektleiter Reinigung (m/w/d)' },
    { value: 'janitor', label: 'Hausmeister (m/w/d)' },
    { value: 'glass_cleaner', label: 'Glasreiniger (m/w/d)' },
    { value: 'other', label: 'Sonstiges / Initiativbewerbung' }
  ];
  
  return (
    <>
      <HeroSection className="hero">
        <div className="container">
          <HeroContent className="hero-content">
            <h1>Karriere bei GuardiansMoves</h1>
            <p>Werden Sie Teil unseres Teams und entdecken Sie vielfältige berufliche Möglichkeiten in einem dynamischen Unternehmen</p>
            <Button href="#bewerbung" className="btn">Jetzt bewerben</Button>
          </HeroContent>
        </div>
      </HeroSection>

      <JobIntro className="job-intro">
        <div className="container">
          <IntroContent className="intro-content">
            <IntroText className="intro-text">
              <h2>Warum GuardiansMoves?</h2>
              <p>Bei GuardiansMoves bieten wir mehr als nur einen Arbeitsplatz. Wir schaffen ein Umfeld, in dem Sie sich beruflich und persönlich weiterentwickeln können. Als wachsendes Unternehmen mit vielfältigen Dienstleistungsbereichen bieten wir zahlreiche Karrieremöglichkeiten und setzen auf ein kollegiales Miteinander.</p>
              <p>Unsere Mitarbeiter sind unser wertvollstes Gut. Daher legen wir großen Wert auf faire Arbeitsbedingungen, respektvolle Kommunikation und kontinuierliche Weiterbildungsmöglichkeiten. Werden Sie Teil unseres Teams und gestalten Sie mit uns gemeinsam die Zukunft von GuardiansMoves!</p>
            </IntroText>
            <IntroImage className="intro-image">
              <img src="/images/logo_outline.png" alt="Team bei GuardiansMoves" />
            </IntroImage>
          </IntroContent>
        </div>
      </JobIntro>

      <JobCategories className="job-categories">
        <div className="container">
          <SectionTitle className="section-title">
            <h2>Unsere Stellenangebote</h2>
          </SectionTitle>
          <CategoriesGrid className="categories-grid">
            {jobCategories.map((category, index) => (
              <CategoryCard key={index} className="category-card">
                <CategoryContent className="category-content">
                  <h3>{category.title}</h3>
                  <p>{category.description}</p>
                  <JobList className="job-list">
                    {category.positions.map((position, idx) => (
                      <li key={idx}>{position}</li>
                    ))}
                  </JobList>
                </CategoryContent>
              </CategoryCard>
            ))}
          </CategoriesGrid>
        </div>
      </JobCategories>

      <Benefits className="benefits">
        <div className="container">
          <SectionTitle className="section-title">
            <h2>Ihre Vorteile bei uns</h2>
          </SectionTitle>
          <BenefitsGrid className="benefits-grid">
            {benefits.map((benefit, index) => (
              <BenefitCard key={index} className="benefit-card">
                <BenefitIcon className="benefit-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                    {benefit.icons ? (
                      benefit.icons.map((path, i) => <path key={i} d={path} />)
                    ) : (
                      <path d={benefit.icon} />
                    )}
                  </svg>
                </BenefitIcon>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </BenefitCard>
            ))}
          </BenefitsGrid>
        </div>
      </Benefits>

      <ApplicationMethods id="bewerbung" className="application-methods">
        <div className="container">
          <SectionTitle className="section-title">
            <h2>So können Sie sich bewerben</h2>
          </SectionTitle>
          <MethodsGrid className="methods-grid">
            {applicationMethods.map((method, index) => (
              <MethodCard key={index} className="method-card">
                <MethodIcon className="method-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
                    {method.icons ? (
                      method.icons.map((path, i) => <path key={i} d={path} />)
                    ) : (
                      <path d={method.icon} />
                    )}
                  </svg>
                </MethodIcon>
                <h3>{method.title}</h3>
                <p>{method.description}</p>
                <MethodButton 
                  href={method.link} 
                  className="btn" 
                  target={method.external ? "_blank" : undefined}
                  rel={method.external ? "noopener noreferrer" : undefined}
                >
                  {method.text}
                </MethodButton>
              </MethodCard>
            ))}
          </MethodsGrid>
          
          <ApplicationForm id="bewerbungsformular" className="application-form">
            <FormTitle className="form-title">
              <h3>Online-Bewerbungsformular</h3>
              <p>Bitte füllen Sie alle mit * markierten Pflichtfelder aus.</p>
            </FormTitle>
            
            <FormInfo>
              <p><strong>Hinweis:</strong> Bitte geben Sie die gewünschte Position an, für die Sie sich bewerben möchten. Wir werden Ihre Anfrage prüfen und Sie anschließend per E-Mail kontaktieren, um den weiteren Bewerbungsprozess zu besprechen und Ihre Unterlagen anzufordern.</p>
            </FormInfo>
            
            {submitSuccess ? (
              <SuccessMessage>
                <h3>Vielen Dank für Ihre Bewerbung!</h3>
                <p>Wir haben Ihre Anfrage erhalten und werden sie sorgfältig prüfen. Bei Interesse werden wir uns umgehend mit Ihnen in Verbindung setzen, um den weiteren Bewerbungsprozess zu besprechen.</p>
                <button onClick={resetForm}>Neues Formular</button>
              </SuccessMessage>
            ) : (
              <form action="https://formsubmit.co/info@guardiansmoves.de" method="POST">
                {submitError && <ErrorMessage>{submitError}</ErrorMessage>}
                    
                <input type="hidden" name="_subject" value="Neue Bewerbungsanfrage über die Website" />
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_template" value="table" />
                <input type="hidden" name="_next" value={window.location.origin + window.location.pathname + "?success=true"} />
                    
                <FormRow className="form-row">
                  <FormGroup className="form-group">
                    <label htmlFor="firstname">Vorname *</label>
                    <input 
                      type="text" 
                      id="firstname" 
                      name="Vorname" 
                      required 
                    />
                  </FormGroup>
                  <FormGroup className="form-group">
                    <label htmlFor="lastname">Nachname *</label>
                    <input 
                      type="text" 
                      id="lastname" 
                      name="Nachname" 
                      required 
                    />
                  </FormGroup>
                </FormRow>
                <FormRow className="form-row">
                  <FormGroup className="form-group">
                    <label htmlFor="email">E-Mail *</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="E-Mail" 
                      required 
                    />
                  </FormGroup>
                  <FormGroup className="form-group">
                    <label htmlFor="phone">Telefon *</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="Telefon" 
                      required 
                    />
                  </FormGroup>
                </FormRow>
                <FormGroup className="form-group">
                  <label htmlFor="address">Anschrift</label>
                  <input 
                    type="text" 
                    id="address" 
                    name="Anschrift" 
                    placeholder="Straße, Hausnummer, PLZ, Ort"
                  />
                </FormGroup>
                <FormGroup className="form-group">
                  <label htmlFor="position">Gewünschte Position *</label>
                  <select 
                    id="position" 
                    name="Position" 
                    required
                    onChange={(e) => {
                      setShowOtherPosition(e.target.value === 'other');
                    }}
                  >
                    {positionOptions.map((option, index) => (
                      <option key={index} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </FormGroup>
                {showOtherPosition && (
                  <FormGroup className="form-group" id="otherPositionGroup">
                    <label htmlFor="otherPosition">Ihre gewünschte Position</label>
                    <input 
                      type="text" 
                      id="otherPosition" 
                      name="Andere_Position"
                    />
                  </FormGroup>
                )}
                <FormGroup className="form-group">
                  <label htmlFor="message">Ihre Nachricht / Kurze Motivation (optional)</label>
                  <textarea 
                    id="message" 
                    name="Nachricht" 
                    placeholder="Erzählen Sie uns kurz etwas über sich und Ihre Motivation für die Bewerbung"
                  ></textarea>
                </FormGroup>
                
                <FormGroup className="form-group checkbox">
                  <CheckboxGroup>
                    <input 
                      type="checkbox" 
                      id="privacy" 
                      name="Datenschutz_akzeptiert"
                      required 
                    />
                    <label htmlFor="privacy">
                      Ich habe die <a href="/datenschutz/" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a> gelesen und akzeptiere diese. *
                    </label>
                  </CheckboxGroup>
                </FormGroup>
                
                <SubmitButton 
                  type="submit" 
                  className="btn"
                >
                  Bewerbung absenden
                </SubmitButton>
              </form>
            )}
          </ApplicationForm>
        </div>
      </ApplicationMethods>
      
      <ScrollToTopButton />
    </>
  );
}

export default Jobs;